import { EditOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, Modal, Space, Tag, Tooltip } from 'antd'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import AtomButton from '@/components/Atoms/AtomButton/AtomButton'
import AtomSelect from '@/components/Atoms/AtomSelect/AtomSelect'
import AtomSwitch from '@/components/Atoms/AtomSwitch/AtomSwitch'
import AtomTable from '@/components/Atoms/AtomTable/AtomTable'
import HoverElement from '@/components/Atoms/HoverElement/HoverElement'
import Loader from '@/components/Atoms/Loader/Loader'
import StoreCreationModal from '@/components/EntityCreationModals/StoreCreationModal/StoreCreationModal'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import { handleApiError } from '@/utils/api/errors/handleApiError'
import { getExchangesAccess } from '@/utils/auth/routeAccess'
import { sortStores } from '@/utils/search/stores/sortStores'
import { getColumnWidths } from '@/utils/table/getColumnWidths'

import { stockImportsApi } from '../StockImports/api/stockImportsApi'
import StockImportsTab from '../StockImports/StockImportsTab/StockImportsTab'
import cn from '../StockImports/StockImportsTab/StockImportsTab.module.scss'
import type { StockImportsTableColumn } from '../StockImports/StockImportsVirtualTable/StockImportsVirtualTable'
import {
  ProductSupplierDto,
  StoreModel,
} from '../StockImports/types/stockImports.types'
import tabCn from './StoreManagementTab.module.scss'

const ALL_SUPPLIERS = 'all'

const MANAGEMENT_SECTION_TABS = [
  'Управление складами',
  'Настройки загрузки',
  'Журнал загрузок',
] as const
type ManagementSectionName = (typeof MANAGEMENT_SECTION_TABS)[number]

const nowrap = (value: string) => <span className={cn.nowrapCell}>{value}</span>

const textWithTooltip = (
  value: string | null | undefined,
  maxLength: number
) => {
  const text = value?.trim() || '—'
  if (text === '—') return text

  const content = (
    <span className={cn.truncatedCell}>
      {text.length > maxLength ? `${text.slice(0, maxLength)}...` : text}
    </span>
  )
  return text.length > maxLength ? (
    <Tooltip title={text}>{content}</Tooltip>
  ) : (
    content
  )
}

function StoreManagementTab() {
  const [modal, contextHolder] = Modal.useModal()
  const { selectedOrganisation, tokenRoles } = useUserInfoStore(
    (state) => state
  )
  const isOrgSelected = Object.keys(selectedOrganisation).length > 0
  const { canManageStores, isStockManager: canUseProductSuppliers } =
    getExchangesAccess({ roles: null, tokenRoles })
  const canEditStores = canManageStores && canUseProductSuppliers

  const sectionTabs = useMemo<ManagementSectionName[]>(
    () =>
      canUseProductSuppliers
        ? [...MANAGEMENT_SECTION_TABS]
        : ['Управление складами'],
    [canUseProductSuppliers]
  )
  const [activeSection, setActiveSection] = useState<ManagementSectionName>(
    'Управление складами'
  )
  const [stockImportsMounted, setStockImportsMounted] = useState(false)

  useEffect(() => {
    if (!sectionTabs.includes(activeSection))
      setActiveSection('Управление складами')
  }, [sectionTabs, activeSection])

  useEffect(() => {
    if (activeSection !== 'Управление складами') setStockImportsMounted(true)
  }, [activeSection])

  const [stores, setStores] = useState<StoreModel[]>([])
  const [suppliers, setSuppliers] = useState<ProductSupplierDto[]>([])
  const [loading, setLoading] = useState(false)
  const [suppliersLoading, setSuppliersLoading] = useState(false)
  const [storeModalOpen, setStoreModalOpen] = useState(false)
  const [editingStore, setEditingStore] = useState<StoreModel | null>(null)
  const [storeModalKey, setStoreModalKey] = useState(0)
  const [supplierFilter, setSupplierFilter] = useState(ALL_SUPPLIERS)

  const loadData = useCallback(async () => {
    if (!canManageStores) return

    setLoading(true)
    try {
      const storeList = await stockImportsApi.getStores()
      setStores(storeList)
    } catch (error) {
      handleApiError(error, modal)
    } finally {
      setLoading(false)
    }
  }, [canManageStores, modal])

  useEffect(() => {
    loadData()
  }, [loadData])

  const ensureSuppliersLoaded = useCallback(async () => {
    if (!canEditStores) return false
    if (suppliers.length > 0) return true

    setSuppliersLoading(true)
    try {
      const supplierList = await stockImportsApi.getProductSuppliers()
      setSuppliers(supplierList)
      return true
    } catch {
      toast.error('Нет доступа к справочнику поставщиков')
      return false
    } finally {
      setSuppliersLoading(false)
    }
  }, [canEditStores, suppliers.length])

  useEffect(() => {
    if (canUseProductSuppliers) ensureSuppliersLoaded()
  }, [canUseProductSuppliers, ensureSuppliersLoaded])

  const supplierFilterOptions = useMemo(
    () => [
      { value: ALL_SUPPLIERS, label: 'Все поставщики' },
      ...suppliers.map((currentSupplier) => ({
        value: currentSupplier.id,
        label: currentSupplier.description,
      })),
    ],
    [suppliers]
  )

  const filteredStores = useMemo(
    () =>
      supplierFilter === ALL_SUPPLIERS
        ? stores
        : stores.filter(
            (store) => store.productSupplierShortDto?.id === supplierFilter
          ),
    [stores, supplierFilter]
  )

  const openStoreModal = useCallback(
    async (store: StoreModel | null) => {
      if (!canEditStores) return

      const canOpen = await ensureSuppliersLoaded()
      if (!canOpen) return

      setEditingStore(store)
      setStoreModalKey((key) => key + 1)
      setStoreModalOpen(true)

      stockImportsApi
        .getProductSuppliers({ force: true })
        .then(setSuppliers)
        .catch(() => {
          // ignore
        })
    },
    [canEditStores, ensureSuppliersLoaded]
  )

  const columns = useMemo<StockImportsTableColumn<StoreModel>[]>(() => {
    const baseColumns: StockImportsTableColumn<StoreModel>[] = [
      {
        header: 'Наименование',
        id: 'description',
        accessorKey: 'description',
        width: 'minmax(130px, 0.6fr)',
      },
      {
        id: 'productSupplier',
        header: 'Поставщик',
        width: 'minmax(180px, 0.75fr)',
        cell: ({ row }) =>
          textWithTooltip(
            row.original.productSupplierShortDto?.description,
            24
          ),
      },
      {
        header: 'Адрес',
        id: 'address',
        accessorKey: 'address',
        width: 'minmax(230px, 0.85fr)',
        cell: ({ row }) => textWithTooltip(row.original.address, 34),
      },
      {
        header: 'Контактные лица',
        id: 'contactPersons',
        accessorKey: 'contactPersons',
        width: 'minmax(200px, 0.75fr)',
        cell: ({ row }) => textWithTooltip(row.original.contactPersons, 28),
      },
      {
        header: 'Комментарий',
        id: 'comment',
        accessorKey: 'comment',
        width: 'minmax(150px, 0.55fr)',
        cell: ({ row }) => textWithTooltip(row.original.comment, 22),
      },
      {
        header: () => nowrap('Код'),
        id: 'code',
        accessorKey: 'code',
        width: 'minmax(70px, 0.25fr)',
      },
      {
        id: 'etaDays',
        header: () => nowrap('ETA Min / Max'),
        width: 'minmax(130px, 0.42fr)',
        cell: ({ row }) =>
          nowrap(`${row.original.etaMinDays} / ${row.original.etaMaxDays}`),
      },
      {
        header: () => nowrap('Отсечка'),
        id: 'etaCutoffTime',
        accessorKey: 'etaCutoffTime',
        width: 'minmax(112px, 0.35fr)',
        cell: ({ row }) => nowrap(row.original.etaCutoffTime),
      },
      {
        id: 'isRemote',
        header: () => nowrap('Удаленный'),
        width: 'minmax(108px, 0.35fr)',
        cell: ({ row }) => nowrap(row.original.isRemote ? 'Да' : 'Нет'),
      },
      {
        id: 'isActive',
        header: () => nowrap('Статус'),
        width: 'minmax(120px, 0.35fr)',
        cell: ({ row }) => (
          <Tag color={row.original.isActive ? 'green' : 'red'}>
            {row.original.isActive ? 'Активен' : 'Неактивен'}
          </Tag>
        ),
      },
    ]

    if (!canEditStores) return baseColumns

    return [
      ...baseColumns,
      {
        id: 'storeActions',
        header: 'Действия',
        width: '76px',
        cell: ({ row }) => (
          <Space>
            <Tooltip title="Редактировать">
              <Button
                icon={<EditOutlined />}
                size="small"
                loading={suppliersLoading}
                onClick={() => openStoreModal(row.original)}
              />
            </Tooltip>
          </Space>
        ),
      },
    ]
  }, [canEditStores, openStoreModal, suppliersLoading])

  const handleStoreSaved = (savedStore: StoreModel) => {
    setStoreModalOpen(false)
    setStores((prev) => {
      const existingIndex = prev.findIndex(
        (store) => store.id === savedStore.id
      )
      if (existingIndex === -1) return sortStores([...prev, savedStore])

      const nextStores = [...prev]
      nextStores[existingIndex] = savedStore
      return sortStores(nextStores)
    })
  }

  return (
    <div className={cn.stockImportsTab}>
      {contextHolder}
      {!canManageStores ? (
        <div className={cn.emptyState}>
          У вас нет доступа к управлению складами
        </div>
      ) : (
        <>
          {sectionTabs.length > 1 && (
            <div className={cn.sectionTabs}>
              <AtomSwitch
                options={sectionTabs.map((name) => ({
                  value: name,
                  label: name,
                }))}
                value={activeSection}
                onChange={setActiveSection}
                ariaLabel="Склады"
                isOrganisationSelected={isOrgSelected}
              />
            </div>
          )}

          {activeSection === 'Управление складами' && (
            <div className={cn.sectionContent}>
              {(canEditStores || canUseProductSuppliers) && (
                <div
                  className={`${cn.sectionHeader} ${canEditStores ? '' : tabCn.sectionHeaderEnd}`}
                >
                  {canUseProductSuppliers && (
                    <div className={tabCn.supplierFilter}>
                      <HoverElement>
                        <AtomSelect
                          openByHover={false}
                          outlined
                          showSearch
                          allowClear={false}
                          value={supplierFilter}
                          options={supplierFilterOptions}
                          loading={suppliersLoading}
                          isOrganisationSelected={isOrgSelected}
                          handleChange={(supplierId) => {
                            if (typeof supplierId === 'string')
                              setSupplierFilter(supplierId)
                          }}
                        />
                      </HoverElement>
                    </div>
                  )}
                  {canEditStores && (
                    <AtomButton
                      icon={<PlusOutlined />}
                      loading={suppliersLoading}
                      onClick={() => openStoreModal(null)}
                    >
                      Добавить склад
                    </AtomButton>
                  )}
                </div>
              )}

              {loading ? (
                <Loader size={24} />
              ) : (
                <AtomTable
                  ariaLabel="Склады"
                  data={filteredStores}
                  columns={columns}
                  getRowId={(row) => row.id}
                  columnWidths={getColumnWidths(columns)}
                  isOrganisationSelected={isOrgSelected}
                />
              )}

              {canEditStores && (
                <StoreCreationModal
                  key={storeModalKey}
                  open={storeModalOpen}
                  supplier={null}
                  suppliers={suppliers}
                  showSupplierSelect
                  store={editingStore}
                  stores={stores}
                  modal={modal}
                  onClose={() => setStoreModalOpen(false)}
                  onSaved={handleStoreSaved}
                />
              )}
            </div>
          )}

          {sectionTabs.length > 1 && stockImportsMounted && (
            <div
              className={
                activeSection === 'Управление складами'
                  ? tabCn.hiddenSection
                  : tabCn.stockImportsSection
              }
            >
              <StockImportsTab
                activeSection={
                  activeSection === 'Журнал загрузок'
                    ? 'Журнал загрузок'
                    : 'Настройки загрузки'
                }
                onActiveSectionChange={(section) => setActiveSection(section)}
                hideSectionToggle
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default StoreManagementTab
